/**
 * Evidence list — agent citations as interactive "exhibits".
 *
 * Each citation is rendered like a court exhibit:
 *   - Tabular-mono ordinal on the left (01, 02, …)
 *   - Citation text as the primary body
 *   - Type-coloured chip below (Entity = primary copper, Document = info blue)
 *   - Animated ↗ on the right that telegraphs the open action
 *
 * Click behaviour:
 *   - `doc:<UUID>`  → opens the universal DocumentDetailOverlay above the page
 *   - `node:<elId>` → opens a right-side Sheet with SidebarEntityDetail
 *   - Unrecognised → row is non-interactive (no hover affordance, no cursor)
 *
 * Accepts either the typed `evidence` array (from a snapshotted Investigation)
 * or the raw `structuredOutput` blob (from a HypothesisRun) for backwards
 * compatibility with the older research-detail call site.
 *
 * A citation saved with a report carries its source's name (`label`, ADR-0097):
 * it is shown as is and no lookup runs for it. A citation whose reference was
 * dropped at save time has no `source` and renders as text only.
 */

import { ArrowUpRight, FileText, Hexagon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDocumentOverlayOptional } from '@/components/document-overlay/DocumentDetailOverlayContext'
import { EvidenceReferenceButton } from '@/components/evidence/EvidenceReferenceButton'
import { EntityDetailSheet } from '@/components/investigations/entity-detail/EntityDetailSheet'
import { useEntityDetails } from '@/components/neo4j/shared/useEntityDetails'
import type { DocumentOverlayTarget } from '@/features/document-overlay/focus'
import { documentMoment } from '@/features/source-snippets/reference'
import { useDocumentName } from '@/hooks/use-document-name'
import { cn } from '@/utils/cn'
import { Section } from './Section'

// ─── Source-ref parsing ──────────────────────────────────────────────────────

// ``id`` may be ``null`` for ``doc:`` refs when the value after ``doc:`` is
// not a UUID — the LLM occasionally emits ``doc:<filename>`` directly. In
// that case ``tail`` carries the verbatim filename for display, and we
// skip the document-id API lookup.
type SourceRef =
  | { kind: 'doc'; id: string | null; raw: string; tail: string; atSeconds: number | null }
  | { kind: 'entity'; id: string; raw: string; tail: string }
  | { kind: 'evidence'; id: string; raw: string; tail: string }
  | { kind: 'unknown'; id: null; raw: string; tail: string }

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function parseSource(source: unknown): SourceRef {
  const raw = typeof source === 'string' ? source.trim() : ''
  if (!raw) return { kind: 'unknown', id: null, raw: '', tail: '' }
  if (raw.startsWith('evidence:') && UUID_RE.test(raw.slice(9))) {
    return { kind: 'evidence', id: raw.slice(9), raw, tail: '' }
  }
  if (raw.startsWith('doc:')) {
    // A UUID (the contracted format), with or without a moment of a recording after it
    // (`#t=<seconds>`, PRD-0074) — or a filename, whose last-6-char hash tail is useless
    // (renders as ``72.PDF``), so the full value is the label and the API lookup is off.
    const moment = documentMoment(raw)
    if (moment) {
      return {
        kind: 'doc',
        id: moment.documentId,
        raw,
        tail: moment.documentId.slice(-6),
        atSeconds: moment.atSeconds,
      }
    }
    return { kind: 'doc', id: null, raw, tail: raw.slice(4), atSeconds: null }
  }
  if (raw.startsWith('node:')) {
    // Strip the `node:` prefix; keep the rest as the Neo4j element id
    // (`<bucket>:<caseId>:<internalId>`) that the entity-detail hook expects.
    const id = raw.slice(5)
    const lastColon = id.lastIndexOf(':')
    const tail = lastColon >= 0 ? id.slice(lastColon + 1) : id.slice(-6) || id
    return { kind: 'entity', id, raw, tail }
  }
  return { kind: 'unknown', id: null, raw, tail: raw.slice(-6) || raw }
}

// ─── Main component ──────────────────────────────────────────────────────────

export function EvidenceCard({
  evidence,
  structuredOutput,
  onOpenEntity,
  highlightIndex,
}: {
  evidence?: unknown[] | null
  structuredOutput?: Record<string, unknown> | null
  /** Override the built-in entity Sheet. Hosts that already live inside a
   * modal (the chat research sheet) pass this to drill in instead of
   * stacking a second sheet on top. */
  onOpenEntity?: (nodeId: string) => void
  /** 0-based index of the row to scroll to and flash — set when an [S#]
   * citation in the report is clicked. */
  highlightIndex?: number | null
}) {
  const { t } = useTranslation()
  const { caseId } = useParams<{ caseId: string }>()
  const docOverlay = useDocumentOverlayOptional()
  const [openEntityId, setOpenEntityId] = useState<string | null>(null)
  const rowRefs = useRef<(HTMLLIElement | null)[]>([])

  // Scroll the cited row into view when a report [S#] chip targets it.
  useEffect(() => {
    if (highlightIndex == null) return
    rowRefs.current[highlightIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [highlightIndex])

  // Auto-close the entity Sheet when a document overlay opens from inside it
  // (e.g. user clicked a Source-documents link in SidebarEntityDetail). The
  // Sheet is a Radix Dialog with `modal=true` — it focus-traps and swallows
  // pointer events on layers rendered above it, so the user couldn't close
  // the document overlay (X button or backdrop click) without this. Letting
  // the Sheet collapse first ensures only one modal layer is active at a time
  // and the document overlay receives clicks normally.
  const docOverlayOpen = Boolean(docOverlay?.isOpen)
  useEffect(() => {
    if (docOverlayOpen && openEntityId !== null) {
      setOpenEntityId(null)
    }
  }, [docOverlayOpen, openEntityId])

  const list = evidence ?? (structuredOutput?.evidence as unknown[] | undefined) ?? null
  if (!Array.isArray(list) || list.length === 0) return null

  return (
    <>
      <Section
        title={t('search.detail.evidence.title')}
        hint={t('search.detail.evidence.hint', { count: list.length })}
      >
        <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
          {t(
            'search.detail.evidence.intro',
            'Each citation is grounded in a case document or an entity from the objects-set. Click a row to inspect the underlying source — document pages open in a preview overlay, entities slide out in a detail panel.'
          )}
        </p>
        <ol className="flex flex-col gap-2">
          {list.map((e, i) => {
            const obj = (typeof e === 'object' && e !== null ? e : {}) as Record<string, unknown>
            const text = (obj.text as string) ?? JSON.stringify(e)
            const label = typeof obj.label === 'string' && obj.label ? obj.label : null
            const ref = parseSource(obj.source)
            return (
              <li
                key={`evidence-${i}`}
                ref={el => {
                  rowRefs.current[i] = el
                }}
              >
                {ref.kind === 'evidence' && caseId ? (
                  <EvidenceReferenceButton caseId={caseId} referenceId={ref.id}>
                    S{i + 1} · {text}
                    {label && <span className="text-muted-foreground"> — {label}</span>}
                  </EvidenceReferenceButton>
                ) : (
                  <CitationRow
                    index={i}
                    citation={{ text, label }}
                    refSource={ref}
                    highlighted={highlightIndex === i}
                    onOpenDoc={target => docOverlay?.open(target)}
                    onOpenEntity={onOpenEntity ?? (id => setOpenEntityId(id))}
                  />
                )}
              </li>
            )
          })}
        </ol>
      </Section>

      {!onOpenEntity && (
        <EntityDetailSheet
          caseId={caseId ?? null}
          nodeId={openEntityId}
          onClose={() => setOpenEntityId(null)}
        />
      )}
    </>
  )
}

// ─── Citation row ────────────────────────────────────────────────────────────

interface CitationRowProps {
  index: number
  /** The claim and the source's saved name, if any. */
  citation: { text: string; label: string | null }
  refSource: SourceRef
  highlighted?: boolean
  onOpenDoc: (target: DocumentOverlayTarget) => void
  onOpenEntity: (id: string) => void
}

function CitationRow({
  index,
  citation: { text, label },
  refSource: ref,
  highlighted,
  onOpenDoc,
  onOpenEntity,
}: CitationRowProps) {
  const { t } = useTranslation()
  // ``doc`` refs without a UUID (LLM emitted ``doc:<filename>``) can't be
  // opened — there's nothing to look up. Keep the row visually inert in
  // that case so the hover/arrow affordance doesn't lie about being
  // clickable.
  const canOpen = (ref.kind === 'doc' && ref.id !== null) || ref.kind === 'entity'

  const handleClick = () => {
    if (!canOpen || ref.id === null) return
    if (ref.kind === 'doc') onOpenDoc({ documentId: ref.id, atSeconds: ref.atSeconds })
    else if (ref.kind === 'entity') onOpenEntity(ref.id)
  }

  // Label matches the report's [S#] citation tokens (S1, S2, …).
  const ordinal = `S${index + 1}`

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!canOpen}
      aria-label={
        canOpen
          ? t('search.detail.evidence.openLabel', {
              kind:
                ref.kind === 'doc'
                  ? t('search.detail.evidence.kind.document', 'document')
                  : t('search.detail.evidence.kind.entity', 'entity'),
              tail: ref.tail,
              defaultValue: 'Open {{kind}} {{tail}}',
            })
          : undefined
      }
      className={cn(
        // Layout: ordinal | content | arrow
        'group relative grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-2 rounded-md border bg-muted/20 px-2.5 py-2.5 text-left transition-all duration-150 md:gap-3 md:px-3',
        canOpen
          ? 'cursor-pointer border-border/50 hover:border-primary/40 hover:bg-muted/40 hover:shadow-[0_1px_0_0_var(--border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
          : 'cursor-default border-border/30',
        // Flash when targeted by a report citation.
        highlighted && 'border-primary bg-primary/10 ring-2 ring-primary'
      )}
    >
      {/* Ordinal — court-exhibit feel: tabular mono inside a bordered chip */}
      <span
        aria-hidden
        className={cn(
          'mt-0.5 inline-flex size-7 flex-none items-center justify-center rounded-sm border bg-background font-mono text-3xs font-medium tabular-nums transition-colors',
          canOpen
            ? 'border-border/60 text-muted-foreground group-hover:border-primary/40 group-hover:text-foreground'
            : 'border-border/40 text-muted-foreground/60'
        )}
      >
        {ordinal}
      </span>

      {/* Body — citation text + type chip */}
      <div className="flex min-w-0 flex-col gap-2">
        <span className="break-words text-sm leading-relaxed text-foreground">{text}</span>
        {(ref.kind === 'doc' || ref.kind === 'entity') && (
          <TypeChip refSource={ref} savedName={label} />
        )}
        {ref.kind === 'unknown' && ref.raw && (
          <span className="break-all font-mono text-2xs text-muted-foreground/70">{ref.raw}</span>
        )}
      </div>

      {/* Open affordance — animates up-right on hover */}
      {canOpen ? (
        <ArrowUpRight
          aria-hidden
          className="mt-1 size-4 flex-none text-muted-foreground/40 transition-all duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary"
        />
      ) : (
        // Spacer to preserve grid alignment when there's no arrow
        <span className="mt-1 size-4 flex-none" aria-hidden />
      )}
    </button>
  )
}

// ─── Type chip ───────────────────────────────────────────────────────────────

// Truncate long filenames so the chip stays one line; full filename is
// exposed via ``title`` for hover. 32 chars matches the cap used in the
// Create Relationship dialog.
const FILENAME_CHIP_MAX = 32

function truncateFilename(name: string, maxLen: number = FILENAME_CHIP_MAX): string {
  return name.length > maxLen ? `${name.slice(0, maxLen - 1)}…` : name
}

/** Primary, human-meaningful label (Person, Company, …) — skips Neo4j's
 * internal `__`-prefixed labels and the generic `Node`. */
function entityType(labels: string[] | undefined): string | undefined {
  return labels?.find(l => !l.startsWith('__') && l !== 'Node')
}

function TypeChip({
  refSource,
  savedName,
}: {
  refSource: Extract<SourceRef, { kind: 'doc' | 'entity' }>
  /** The source's name saved with the citation; no lookup runs when it is set. */
  savedName: string | null
}) {
  const { kind, tail } = refSource
  const docId = kind === 'doc' ? refSource.id : null
  const entityId = kind === 'entity' ? refSource.id : null
  const { t } = useTranslation()
  const { caseId } = useParams<{ caseId: string }>()
  const Icon = kind === 'doc' ? FileText : Hexagon

  // Resolve the entity's actual type for the label (Person/Company/…) instead
  // of a generic "Entity". Cached + deduped per node; gated to entity rows.
  const entityQ = useEntityDetails({
    caseId: caseId ?? '',
    selectedNode:
      kind === 'entity' && entityId && !savedName
        ? { id: entityId, name: '', labels: [], properties: {} }
        : null,
    selectedEdge: null,
  })
  const resolvedType = entityType(entityQ.fullNodeDetails?.labels)
  const label =
    kind === 'doc'
      ? t('search.detail.evidence.kind.document', 'Document')
      : (resolvedType ?? t('search.detail.evidence.kind.entity', 'Entity'))

  // For document chips, prefer the API-resolved filename. When the source
  // wasn't a UUID (``docId === null`` because the LLM emitted
  // ``doc:<filename>`` directly) the ``tail`` already carries the
  // verbatim filename, so we use that as the fall-back. Entities get no
  // suffix — the element-id tail (e.g. "195") is meaningless to the reader.
  const docNameQ = useDocumentName(kind === 'doc' && !savedName ? docId : null)
  const apiFilename = docNameQ.data ?? null
  const rawSuffix = savedName ?? (kind === 'doc' ? (apiFilename ?? tail ?? '') : '')
  const displaySuffix = truncateFilename(rawSuffix)
  // Only expose the full string via ``title`` when truncation actually hid
  // something — otherwise the tooltip just duplicates the visible label.
  const suffixTitle = rawSuffix.length > FILENAME_CHIP_MAX ? rawSuffix : undefined

  return (
    <span
      className={cn(
        // max-w-full + a truncating suffix: a long filename must not push the
        // chip past a phone-width row.
        'inline-flex w-fit max-w-full items-center gap-1.5 rounded-full border px-2 py-0.5 text-3xs font-semibold uppercase tracking-wider transition-colors',
        kind === 'doc'
          ? 'border-info/30 bg-info/10 text-info'
          : 'border-primary/30 bg-primary/5 text-primary'
      )}
    >
      <Icon className="size-3 shrink-0" />
      <span className="shrink-0">{label}</span>
      {displaySuffix && (
        // Filenames and names are case-sensitive and look like shouting in
        // uppercase: the suffix always renders in normal case.
        <span
          className="min-w-0 truncate font-mono text-4xs normal-case tracking-normal text-muted-foreground/80"
          title={suffixTitle}
        >
          ·{displaySuffix}
        </span>
      )}
    </span>
  )
}
